import React from 'react';
import { Menu, Badge, Avatar } from 'antd';
import { ExportOutlined, LogoutOutlined, UserOutlined, BellOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { userAtom } from '../../store/atoms';
import { useNavigate } from 'react-router-dom';
import styles from './SidebarContent.module.scss';
import logo from '../../logo.svg';
import layoutStyles from '../../layouts/mainLayout.module.scss';
import {
  SidebarDashboardIcon,
  SidebarInternalIcon,
  SidebarLayoutToggleIcon,
  SidebarRevWordmarkIcon
} from './sidebarInlineIcons';
import { Role, canSeeInternalProjectsNav } from '../../constants/roles';

interface SidebarContentProps {
  selectedKeys: string[];
  onMenuClick: ({ key }: { key: string }) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** MobileDrawer içinde: üstte zaten logo var; tekrar çizmeyi engelle */
  embedInDrawer?: boolean;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  selectedKeys,
  onMenuClick,
  isCollapsed = false,
  onToggleCollapse,
  embedInDrawer = false
}) => {
  const [user] = useAtom(userAtom);
  const navigate = useNavigate();
  const iconStyle = { fontSize: 20, width: 20, height: 20 };
  const effectiveCollapsed = isCollapsed;
  const isDashboardActive = selectedKeys.includes('/dashboard');
  const isInternalActive = selectedKeys.includes('/internal-projects');
  const isExternalActive = selectedKeys.includes('/external-projects');

  const showInternalInMenu = !!user && canSeeInternalProjectsNav(user.role);

  /** rc-menu: motion açılınca yükseklik/slide animasyonu; sidebar’da istemiyoruz */
  const menuMotionOff = {} as React.ComponentProps<typeof Menu>['motion'];

  return (
    <>
      {!embedInDrawer && (
        <div
          className={`${styles.mobileLogo} ${effectiveCollapsed ? styles.collapsed : ''}`}
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        >
          {effectiveCollapsed ? (
            <SidebarRevWordmarkIcon className={styles.logoImage} />
          ) : (
            <img className={styles.logoImage} src={logo} alt="logo" />
          )}
          {onToggleCollapse && (
            <button
              type="button"
              aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={styles.logoToggleButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
            >
              <SidebarLayoutToggleIcon />
            </button>
          )}
        </div>
      )}
      <div className={`${styles.menuContainer} ${embedInDrawer ? styles.menuContainerDrawer : ''}`}>
        <div className={styles.mainMenu}>
          <Menu 
            mode="inline" 
            motion={menuMotionOff}
            selectedKeys={selectedKeys} 
            onSelect={onMenuClick} 
            inlineCollapsed={effectiveCollapsed}
            inlineIndent={16}
            items={[
              ...(user ? [{
                key: '/dashboard',
                icon: <SidebarDashboardIcon style={iconStyle} active={isDashboardActive} />,
                label: 'Dashboard'
              }] : []),
              ...(user && showInternalInMenu ? [{
                key: '/internal-projects',
                icon: <SidebarInternalIcon style={iconStyle} active={isInternalActive} />,
                label: 'Internal Projects'
              }] : []),
              ...(user ? [{
                key: '/external-projects',
                icon: <ExportOutlined style={{ color: isExternalActive ? '#9B86BC' : '#F6FFED', ...iconStyle }} />,
                label: 'External Projects'
              }] : []),
            ]} 
          />
        </div>
        <div className={styles.bottomMenu}>
          <Menu 
            mode="inline" 
            motion={menuMotionOff}
            selectedKeys={selectedKeys} 
            onSelect={onMenuClick} 
            inlineCollapsed={effectiveCollapsed}
            inlineIndent={16}
            items={[
              ...(user
                ? [
                    {
                      key: '/results?type=notifications',
                      icon: (
                        <Badge count={user?.unreadNotificationCount || 0} showZero={false} className={styles.notificationBadge}>
                          <BellOutlined style={iconStyle} />
                        </Badge>
                      ),
                      label: 'Notifications',
                    },
                  ]
                : []),
              ...(user && user.role !== Role.Guest ? [{ key: '/settings', icon: <Avatar
                shape="circle"
                className={`notification-avatar ${layoutStyles.userAvatar} ${styles.sidebarMenuAvatar}`}
                src={user?.thumbnailUrl}
                size={20}
                style={{
                  fontWeight: 500,
                  fontSize: 10,
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  minHeight: 20,
                  lineHeight: 1,
                  border: 'none',
                  borderWidth: 0,
                }}
              >
                {!user?.thumbnailUrl && (
                  <>
                    {user?.firstName?.[0]?.toUpperCase() || ''}{user?.lastName?.[0]?.toUpperCase() || ''}
                  </>
                )}
              </Avatar>, label: 'Settings' }] : []),
              
              { key: user ? 'logout' : 'login', icon: user ? <LogoutOutlined style={iconStyle} /> : <UserOutlined style={iconStyle} />, label: user ? 'Log Out' : 'Login' },
            ]} 
          />
        </div>
      </div>
    </>
  );
};

export default SidebarContent; 